import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { filterToCriteria, ILeadViewFilter } from '../../aida/entities/LeadView/useLeadView';
import type { ILeadServedOutcomeView } from '../../aida/viewModels/leads/interfaces';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'LeadServed/filter';
const PAGE_SIZE = 25;

export function useLeadServedFilterInfiniteQuery({
	filter,
	enabled = true,
	meta,
}: {
	filter: ILeadViewFilter;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	const criteria = filterToCriteria(filter);

	return useInfiniteQuery({
		queryKey: [BASE_QUERY_KEY, userSession.account?.id, criteria],
		queryFn: ({ pageParam }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<ILeadServedOutcomeView>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: BASE_QUERY_KEY,
					// `composeApiUrl` strips empty values, so the first page's '' cursor sends no param.
					queryParams: { pageSize: String(PAGE_SIZE), pageToken: pageParam },
				}),
				'POST',
				{ criteria }
			),
		initialPageParam: '',
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		enabled,
		meta: meta ?? {
			errorLogging: { category: 'LeadServed', action: 'LeadServedFilterInfiniteQuery-Error' },
			toastErrorMessage: 'Failed to load calls',
		},
	});
}

export const invalidateLeadServedFilterQueries = () =>
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
